<template>
  <div class="font-sans">

    <!-- ── Filters bar ── -->
    <div class="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">

      <!-- Search -->
      <SearchForm @search="handleSearch" class="w-full md:w-72" />

      <!-- Filters + Create button -->
      <div class="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end">
        <RadioFilter @filter="handleRadioFilter" />
        <!-- <DropDownFilter :datas="events" field="Status" label="Filter" @filter="handleCheckBoxFilter" /> -->

        <router-link
          to="/admin/event"
          class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-bold
                 bg-primary-400 text-white hover:bg-primary-600
                 hover:-translate-y-0.5 shadow-teal transition-all duration-200"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 4v16m8-8H4"/>
          </svg>
          Create Event
        </router-link>
      </div>
    </div>

    <!-- ── Table ── -->
    <div v-if="events.length > 0" class="overflow-x-auto rounded-xl border border-primary-100 dark:border-dark-600">
      <table class="w-full border-separate border-spacing-0">
        <thead>
          <tr>
            <th class="text-left text-[11px] font-bold uppercase tracking-widest px-4 py-3
                       text-muted dark:text-muted-light
                       bg-primary-50 dark:bg-dark-600
                       border-b border-primary-100 dark:border-dark-500">
              Event Name
            </th>
            <th class="text-left text-[11px] font-bold uppercase tracking-widest px-4 py-3
                       text-muted dark:text-muted-light
                       bg-primary-50 dark:bg-dark-600
                       border-b border-primary-100 dark:border-dark-500">
              Date
            </th>
            <th class="text-left text-[11px] font-bold uppercase tracking-widest px-4 py-3
                       text-muted dark:text-muted-light
                       bg-primary-50 dark:bg-dark-600
                       border-b border-primary-100 dark:border-dark-500">
              Start Time
            </th>
            <th class="text-left text-[11px] font-bold uppercase tracking-widest px-4 py-3
                       text-muted dark:text-muted-light
                       bg-primary-50 dark:bg-dark-600
                       border-b border-primary-100 dark:border-dark-500">
              End Time
            </th>
            <th class="text-center text-[11px] font-bold uppercase tracking-widest px-4 py-3
                       text-muted dark:text-muted-light
                       bg-primary-100 dark:bg-dark-500
                       border-b border-primary-100 dark:border-dark-500">
              Actions
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="event in paginatedEvents"
            :key="event.id"
            class="group hover:bg-primary-50 dark:hover:bg-dark-600/50 transition-colors duration-150 cursor-pointer"
            @click="showOrderDetails(event)"
          >
            <td class="px-4 py-3.5 text-[13px] font-semibold text-dark-700 dark:text-white
                       border-b border-primary-50 dark:border-dark-600 whitespace-nowrap">
              {{ event.title }}
            </td>
            <td class="px-4 py-3.5 text-[13px] text-muted dark:text-muted-light
                       border-b border-primary-50 dark:border-dark-600 whitespace-nowrap">
              {{ $formatDate(event.date) }}
            </td>
            <td class="px-4 py-3.5 text-[13px] text-muted dark:text-muted-light
                       border-b border-primary-50 dark:border-dark-600 whitespace-nowrap">
              {{ event.start_time }}
            </td>
            <td class="px-4 py-3.5 text-[13px] text-muted dark:text-muted-light
                       border-b border-primary-50 dark:border-dark-600 whitespace-nowrap">
              {{ event.end_time }}
            </td>

            <!-- Actions -->
            <td class="px-4 py-3.5 text-[13px] text-muted dark:text-muted-light
                       border-b border-primary-50 dark:border-dark-600 whitespace-nowrap"
                @click.stop>
              <div class="flex items-center justify-center gap-2">

                <!-- View -->
                <router-link
                  v-if = "forClient"
                  :to="`/admin/dashboard/event/${event.event_id}`"
                  class="action-btn hover:bg-primary-100 dark:hover:bg-dark-500
                         hover:text-primary-600 dark:hover:text-primary-300"
                  title="View analytics"
                >
                  <svg class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"/>
                    <path fill-rule="evenodd"
                          d="M.664 10.59a1.651 1.651 0 0 1 0-1.186A10.004 10.004 0 0 1 10 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0 1 10 17c-4.257 0-7.893-2.66-9.336-6.41ZM14 10a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z"
                          clip-rule="evenodd"/>
                  </svg>
                </router-link>

                <!-- Edit (active) -->
                <router-link
                  v-if="!event.has_ended && !forAdmin"
                  :to="`/admin/event/edit/${event.event_id}`"
                  class="action-btn hover:bg-primary-100 dark:hover:bg-dark-500
                         hover:text-primary-600 dark:hover:text-primary-300"
                  title="Edit event"
                >
                  <svg class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M17.414 2.586a2 2 0 0 1 0 2.828l-10 10a2 2 0 0 1-1.414.586H4a1 1 0 0 1-1-1v-2.586a2 2 0 0 1 .586-1.414l10-10a2 2 0 0 1 2.828 0Zm-2.828 1.414L5 13.586V15h1.414l9.586-9.586-1.414-1.414Z"/>
                  </svg>
                </router-link>

                <!-- Edit (disabled — ended) -->
                <button
                  v-else
                  class="action-btn opacity-30 cursor-not-allowed"
                  title="This event has already occurred and cannot be edited"
                  disabled
                >
                  <svg class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M17.414 2.586a2 2 0 0 1 0 2.828l-10 10a2 2 0 0 1-1.414.586H4a1 1 0 0 1-1-1v-2.586a2 2 0 0 1 .586-1.414l10-10a2 2 0 0 1 2.828 0Zm-2.828 1.414L5 13.586V15h1.414l9.586-9.586-1.414-1.414Z"/>
                  </svg>
                </button>
                
                <!-- View list of attendees -->
                <button
                  v-if="!event.private || event.has_ended "
                  class="action-btn opacity-30 cursor-not-allowed"
                  title="You can not view the list of attendees for public events or events that have already ended"
                  disabled
                >
                  <svg class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                  </svg>
                </button>
                <router-link
                  v-else
                  :to="`/admin/event/attendee/${event.event_id}`"
                  class="action-btn hover:bg-primary-100 dark:hover:bg-dark-500
                         hover:text-primary-600 dark:hover:text-primary-300"
                  title="View list of attendees">
                  <svg class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                  </svg>
                </router-link>

              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <!-- ── Pagination bar ── -->
      <div class="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3.5
                  bg-surface dark:bg-dark-700
                  border-t border-primary-100 dark:border-dark-600">
 
        <!-- Left: showing X-Y of Z + page size -->
        <div class="flex items-center gap-3 text-[12.5px] text-muted dark:text-muted-light">
          <span>
            Showing
            <span class="font-bold text-dark-700 dark:text-white">{{ paginationStart }}–{{ paginationEnd }}</span>
            of
            <span class="font-bold text-dark-700 dark:text-white">{{ filteredEvents.length }}</span>
          </span>
 
          <div class="relative">
            <select
              v-model.number="pageSize"
              class="appearance-none text-[12px] font-bold rounded-lg pl-2.5 pr-7 py-1.5
                     bg-primary-50 dark:bg-dark-600
                     border border-primary-100 dark:border-dark-500
                     text-dark-700 dark:text-white
                     hover:bg-primary-100 dark:hover:bg-dark-500
                     focus:outline-none focus:ring-2 focus:ring-primary-300
                     cursor-pointer transition-colors"
            >
              <option :value="5">5 / page</option>
              <option :value="10">10 / page</option>
              <option :value="25">25 / page</option>
              <option :value="50">50 / page</option>
            </select>
            <svg class="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none
                        text-muted dark:text-muted-light"
                 fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/>
            </svg>
          </div>
        </div>
 
        <!-- Right: page controls -->
        <div class="inline-flex items-center gap-0.5 p-1 rounded-2xl
                    bg-primary-50/70 dark:bg-dark-600/70
                    border border-primary-100 dark:text-muted-light">
 
          <button
            class="nav-btn"
            :disabled="currentPage === 1"
            @click="currentPage = 1"
            title="First page"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M18 19l-7-7 7-7M11 19l-7-7 7-7"/>
            </svg>
          </button>
          <button
            class="nav-btn"
            :disabled="currentPage === 1"
            @click="currentPage--"
            title="Previous page"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
          {{ currentPage }} / {{ totalPages }} page
          <template v-for="(item, idx) in pageItems">
            <span
              v-if="item === '...'"
              :key="`dots-${idx}`"
              class="w-8 h-8 flex items-center justify-center text-[13px] text-muted dark:text-muted-light select-none"
            >
              ⋯
            </span>
            <button
              v-else
              :key="item"
              class="page-num-btn"
              :class="item === currentPage ? 'page-num-btn-active' : ''"
              @click="currentPage = item"
            >
              {{ currentPage === item ? item : item }}
            </button>
          </template>
 
          <button
            class="nav-btn"
            :disabled="currentPage === totalPages"
            @click="currentPage++"
            title="Next page"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
          <button
            class="nav-btn"
            :disabled="currentPage === totalPages"
            @click="currentPage = totalPages"
            title="Last page"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 19l7-7-7-7M13 19l7-7-7-7"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- ── Empty state ── -->
    <div v-else
         class="flex flex-col items-center justify-center py-16 gap-4
                bg-surface dark:bg-dark-700 rounded-card
                border border-primary-100 dark:border-dark-600">
      <div class="w-14 h-14 rounded-2xl bg-primary-50 dark:bg-dark-600
                  flex items-center justify-center">
        <svg class="w-7 h-7 text-muted dark:text-muted-light" fill="none"
             stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
        </svg>
      </div>
      <div class="text-center">
        <p class="font-display font-bold text-[15px] text-dark-700 dark:text-white mb-1">
          No events found
        </p>
        <p class="text-[13px] text-muted dark:text-muted-light">
          Try adjusting your filters or create a new event.
        </p>
      </div>
      <router-link
        to="/admin/event"
        class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-bold
               bg-primary-400 text-white hover:bg-primary-600 transition-all duration-200"
      >
        + Create Event
      </router-link>
    </div>

    <!-- ══════════════════════════════════════════
         Event Detail Modal
    ══════════════════════════════════════════ -->
    <transition name="modal">
      <div
        v-if="selectedEvent"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
        @click.self="selectedEvent = null"
      >
        <div
          class="bg-surface dark:bg-dark-700 rounded-card shadow-dropdown
                 w-full max-w-2xl max-h-[90vh] overflow-y-auto
                 border border-primary-100 dark:border-dark-600"
        >
          <!-- Modal header -->
          <div class="flex items-center justify-between p-6
                      border-b border-primary-100 dark:border-dark-600">
            <div>
              <p class="text-[11px] font-bold uppercase tracking-widest
                        text-muted dark:text-muted-light mb-0.5">
                Event Details
              </p>
              <h2 class="font-display text-[16px] font-bold text-dark-700 dark:text-white">
                {{ selectedEvent.title }}
              </h2>
            </div>
            <button
              @click="selectedEvent = null"
              class="w-9 h-9 rounded-xl bg-primary-50 dark:bg-dark-600
                     flex items-center justify-center
                     text-muted dark:text-muted-light
                     hover:bg-primary-100 dark:hover:bg-dark-500 transition-colors"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- Modal body -->
          <div class="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">

            <!-- Image -->
            <div class="rounded-xl overflow-hidden border border-primary-100 dark:border-dark-600 h-52">
              <img :src="selectedEvent.image_url" alt="Event image"
                   class="w-full h-full object-cover"/>
            </div>

            <!-- Meta -->
            <div class="flex flex-col gap-3">
              <!-- Status badge -->
              <div>
                <span
                  class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold"
                  :class="selectedEvent.status
                    ? 'bg-dark-100 dark:bg-dark-600 text-muted dark:text-muted-light'
                    : 'bg-primary-50 dark:bg-primary-400/15 text-primary-600 dark:text-primary-300'"
                >
                  <span class="w-1.5 h-1.5 rounded-full"
                        :class="selectedEvent.status ? 'bg-muted' : 'bg-primary-400'"/>
                  {{ selectedEvent.status ? 'Not Active' : 'Active' }}
                </span>
              </div>

              <div class="flex flex-col gap-2.5 mt-1">
                <div class="flex items-center gap-2.5">
                  <div class="w-7 h-7 rounded-lg bg-primary-50 dark:bg-dark-600
                              flex items-center justify-center flex-shrink-0">
                    <svg class="w-3.5 h-3.5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                  </div>
                  <div>
                    <p class="text-[10px] font-bold uppercase tracking-widest text-muted dark:text-muted-light">From</p>
                    <p class="text-[13px] font-medium text-dark-700 dark:text-white">
                      {{ $formatDate(selectedEvent.date) }} · {{ selectedEvent.start_time }}
                    </p>
                  </div>
                </div>

                <div class="flex items-center gap-2.5">
                  <div class="w-7 h-7 rounded-lg bg-primary-50 dark:bg-dark-600
                              flex items-center justify-center flex-shrink-0">
                    <svg class="w-3.5 h-3.5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <div>
                    <p class="text-[10px] font-bold uppercase tracking-widest text-muted dark:text-muted-light">To</p>
                    <p class="text-[13px] font-medium text-dark-700 dark:text-white">
                      {{ $formatDate(selectedEvent.end_date) }} · {{ selectedEvent.end_time }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Description -->
          <div class="px-6 pb-4">
            <p class="text-[11px] font-bold uppercase tracking-widest text-muted dark:text-muted-light mb-2">
              Description
            </p>
            <div class="text-[13px] text-dark-400 dark:text-white/70 leading-relaxed
                        bg-primary-50/60 dark:bg-dark-600/60 rounded-xl p-4
                        border border-primary-100 dark:border-dark-500"
                 v-html="selectedEvent.description || 'No description available'"/>
          </div>

          <!-- Modal actions -->
          <div class="flex flex-wrap gap-3 p-6 pt-3
                      border-t border-primary-100 dark:border-dark-600">
            <router-link
              :to="`/admin/dashboard/event/${selectedEvent.event_id}`"
              class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-bold
                     bg-primary-50 dark:bg-dark-600 text-primary-600 dark:text-primary-400
                     border border-primary-200 dark:border-dark-500
                     hover:bg-primary-100 dark:hover:bg-dark-500 transition-colors"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
              Analytics
            </router-link>
            
            <!-- List of attends -->
            <router-link
              v-if="selectedEvent.private"
              :to="`/admin/event/attendee/${selectedEvent.event_id}`"
              class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-bold
                     bg-primary-50 dark:bg-dark-600 text-primary-600 dark:text-primary-400
                     border border-primary-200 dark:border-dark-500
                     hover:bg-primary-100 dark:hover:bg-dark-500 transition-colors"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
              List of Attendees
            </router-link>

            <router-link
              v-if="!selectedEvent.has_ended && !forAdmin"
              :to="`/admin/event/edit/${selectedEvent.event_id}`"
              class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-bold
                     bg-dark-700 dark:bg-primary-400 text-white
                     hover:bg-primary-600 dark:hover:bg-primary-600 transition-colors"
              disabled
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
              </svg>
              Edit Event
            </router-link>

            <button
              @click="confirmDeleteOrder"
              class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-bold
                     bg-red-50 dark:bg-red-950/30 text-red-500
                     border border-red-200 dark:border-red-800/50
                     hover:bg-red-100 dark:hover:bg-red-950/50 transition-colors ml-auto"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
              </svg>
              Delete Event
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import SearchForm from "@/components/SearchForm.vue";
import RadioFilter from "@/components/RadioFilter.vue";
// import DropDownFilter from "@/components/DropDownFilter.vue";

export default {
  name: "EventList",
  components: { SearchForm, RadioFilter},

  props: {
    events: { type: Array, required: true },
  },

  data() {
    return {
      searchFilter: "",
      radioTimefilterVariable: "",
      radioDatefilterVariable: "",
      statusesFilter: [],
      selectedEvent: null,
      // ── Pagination state ──
      currentPage: 1,
      pageSize: 10,
    };
  },

  watch: {
    // Any change to the filters should reset us back to page 1,
    // otherwise the user can land on a page that no longer has rows.
    radioDatefilterVariable() { this.currentPage = 1; },
    statusesFilter() { this.currentPage = 1; },
    searchFilter() { this.currentPage = 1; },
    pageSize() { this.currentPage = 1; },
  },

  methods: {
    handleSearch(search) { this.searchFilter = search; },
    handleRadioFilter(filter) { this.radioDatefilterVariable = filter; },
    handleCheckBoxFilter(filters) { this.statusesFilter = Array.isArray(filters) ? filters : [filters]; },
    showOrderDetails(event) { this.selectedEvent = event; },
  },

  computed: {
    userInfo() {
			return this.$store.state.auth.user || {};
		},
    forAdmin() {
			const type = Number(this.userInfo.user_type);
			return type === 0;
		},
		forClient() {
			const type = Number(this.userInfo.user_type);
			return type !== 1 && type == 2;
		},
    filteredEvents() {
      let events = this.events;
      const today = new Date();

      switch (this.radioDatefilterVariable) {
        case "today": {
          today.setHours(0, 0, 0, 0);
          const tomorrow = new Date(today);
          tomorrow.setDate(today.getDate() + 1);
          events = events.filter(event => {
            const startDate = new Date(event.date);
            return startDate >= today && startDate < tomorrow;
          });
          break;
        }
        case "upcoming": {
          today.setHours(0, 0, 0, 0);
          events = events.filter(event => new Date(event.date) >= today);
          break;
        }
        case "past": {
          const now = new Date();
          events = events.filter(event => new Date(event.end_date) < now);
          break;
        }
      }

      if (this.statusesFilter.length > 0) {
        events = events.filter(event => {
          const statusKey = Object.keys(event).find(k => k.toLowerCase() === "status");
          const value = statusKey ? String(event[statusKey]) : "";
          return this.statusesFilter.includes(value);
        });
      }

      if (this.searchFilter) {
        const term = this.searchFilter.toLowerCase();
        events = events.filter(event => String(event.title).toLowerCase().includes(term));
      }
       // Most recent first — copy the array so we never mutate the prop.
       events = [...events].sort((a, b) => new Date(b.date) - new Date(a.date));
      return events;
    },
     // ── Pagination computeds ──
     totalPages() {
      return Math.max(1, Math.ceil(this.filteredEvents.length / this.pageSize));
    },
 
    paginatedEvents() {
      // Clamp in case filters shrank the list out from under the current page.
      const page = Math.min(this.currentPage, this.totalPages);
      const start = (page - 1) * this.pageSize;
      return this.filteredEvents.slice(start, start + this.pageSize);
    },
 
    paginationStart() {
      if (this.filteredEvents.length === 0) return 0;
      return (Math.min(this.currentPage, this.totalPages) - 1) * this.pageSize + 1;
    },
 
    paginationEnd() {
      return Math.min(
        this.paginationStart + this.pageSize - 1,
        this.filteredEvents.length
      );
    },
 
    // Windowed page numbers (e.g. shows up to 5 around current page)
    visiblePageNumbers() {
      const total = this.totalPages;
      const current = Math.min(this.currentPage, total);
      const windowSize = 5;
      let start = Math.max(1, current - Math.floor(windowSize / 2));
      let end = Math.min(total, start + windowSize - 1);
      start = Math.max(1, end - windowSize + 1);
 
      const pages = [];
      for (let p = start; p <= end; p++) pages.push(p);
      return pages;
    },
  },

};
</script>

<style scoped>
.action-btn {
  @apply flex items-center justify-center w-8 h-8 rounded-xl
         text-muted dark:text-muted-light
         bg-primary-50 dark:bg-dark-600
         transition-all duration-200;
}

/* Modal transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}
.modal-enter-active .bg-surface,
.modal-leave-active .bg-surface {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from .bg-surface {
  transform: scale(0.97) translateY(8px);
}
</style>